/**
 * VENDORED from tc-console/src/mdx/registry.ts — DO NOT EDIT.
 *
 * Refresh with scripts/sync-home.sh; edit the canonical file instead. See the
 * script header for what is copied verbatim and what is an adaptation.
 */
/**
 * registry.ts — the MDX component registry: ONE seam where markdown meets React.
 *
 * WHAT IT IS. Every MDX/markdown page in the product (Guest Home, Learner View
 * Home, anywhere else later) renders through MDXContent, and MDXContent draws
 * its renderers from a registry: element renderers for the standard markdown
 * constructs, and fenced-block renderers keyed by fence language for the richer
 * components. This file is the type both halves share, plus the merge that
 * composes them.
 *
 * WHY A REGISTRY. Content is plain markdown (`.md`/`.mdx`) — never raw HTML and
 * never executable JSX, so the sanitization contract (react-markdown's default:
 * HTML escaped, unsafe URL schemes stripped) holds no matter what is registered.
 * Richness comes from the renderer side, not from trusting the source:
 *
 *   - standard constructs (h1-h6, p, a, lists, blockquote, hr, code, tables,
 *     images, figures) get their polished MUI presentation from the default
 *     element map in mdxComponents.tsx;
 *   - richer components — Callout, Notice, Card, Figure, Badge, Accordion,
 *     Video, H5P, xAPI summaries, skills widgets — are ADDED LATER as fenced
 *     blocks (```notice … ```) or directive syntax, each a registry entry that
 *     receives plain text and returns React. Registering one never touches
 *     MDXContent itself: pass `registry={{ blocks: { callout: … } }}` at the
 *     call site, or extend defaultBlocks once the component is product-ready.
 *
 * The working exemplar is the `notice` fence (defaultBlocks in MDXContent.tsx):
 * a fenced block whose body renders inside a themed MUI Alert. Every future
 * richer component follows that shape.
 */

import type { ReactNode } from 'react'
import type { Components } from 'react-markdown'

/** Renders the body of a fenced code block (```lang … ```) as a rich component.
 *  Receives the raw fence text and the normalized (lowercase) language tag. */
export type MdxBlockRenderer = (code: string, language: string) => ReactNode

export interface MdxComponentRegistry {
  /** Element renderers — react-markdown's own Components shape (p, h1, a, img,
   *  table, …). Overrides here replace the default MUI presentation one element
   *  at a time; everything unspecified stays canonical. */
  elements?: Partial<Components>
  /** Fenced-block renderers by language tag (```notice, ```figure, ```video…).
   *  The extension seam for components markdown has no native syntax for. A
   *  fence whose language has no renderer keeps the default code-block look —
   *  unknown MDX never breaks a page. */
  blocks?: Record<string, MdxBlockRenderer>
}

/** Compose registries left-to-right: later entries win, element-by-element and
 *  block-by-block, so an override never has to re-specify the whole map. */
export function mergeRegistries(...registries: Array<MdxComponentRegistry | undefined>): MdxComponentRegistry {
  const elements: Partial<Components> = {}
  const blocks: Record<string, MdxBlockRenderer> = {}
  for (const r of registries) {
    if (!r) continue
    Object.assign(elements, r.elements)
    Object.assign(blocks, r.blocks)
  }
  return { elements, blocks }
}
